export default {
  index: {
    title: 'Guider om tidevand og strøm',
    description:
      'Lær, hvordan tidevand og tidevandsstrømme fungerer: tidevandskoefficienter, flod- og ebbestrømme, tidevandsreferencer, og hvordan tidevandsforudsigelser beregnes.',
    subtitle:
      'Korte, letforståelige forklaringer på videnskaben bag forudsigelser af tidevand og strøm.',
  },
  back: 'Tilbage til alle guider',
  guides: {
    'tidal-coefficients': {
      title: 'Tidevandskoefficienter forklaret',
      description:
        'Hvad tidevandskoefficienter betyder: skalaen fra 20 til 120, hvordan springflod og niptid hænger sammen med Månen, og hvorfor en højere koefficient giver et større tidevandsspænd.',
      blurb:
        'Skalaen fra 20 til 120, der sammenfatter, hvor stort et tidevand bliver, og hvordan Månen driver det.',
      body: [
        {
          p: 'En tidevandskoefficient er et enkelt tal, der sammenfatter, hvor stort tidevandet bliver på en given dag. Den bruges i vid udstrækning i Frankrig og andre lande, der følger den franske hydrografiske tradition. Koefficienten er dimensionsløs og går normalt fra omkring 20 til 120: jo større tallet er, desto større er forskellen mellem højvande og lavvande, og desto stærkere er de tilhørende tidevandsstrømme.',
        },
        { h2: 'Skalaen fra 20 til 120' },
        {
          p: 'Koefficienten sammenligner dagens tidevandsspænd med en langsigtet reference. Flere punkter på skalaen har en fast betydning:',
        },
        {
          list: [
            { term: '20', text: 'det mindste tidevand, tæt på det mindste astronomiske spænd.' },
            { term: '45', text: 'en gennemsnitlig niptid, når spændet er lille.' },
            { term: '70', text: 'et middeltidevand, cirka midtvejs mellem niptid og springflod.' },
            { term: '95', text: 'en gennemsnitlig springflod, når spændet er stort.' },
            { term: '120', text: 'det største tidevand, tæt på det største astronomiske spænd.' },
          ],
        },
        { h2: 'Hvorfor den ændrer sig: Månen' },
        {
          p: 'Koefficienterne stiger og falder i den cirka to uger lange cyklus mellem springflod og niptid. Springflod med høje koefficienter indtræffer en dag eller to efter hver ny- og fuldmåne, når Solen og Månen trækker på linje, og deres tidevandskræfter lægges sammen. Niptid med lave koefficienter indtræffer nær første og sidste kvarter, når Solen og Månen står vinkelret på hinanden og delvist ophæver hinanden.',
        },
        {
          p: 'Månens afstand har også betydning. Når en springflod falder sammen med perigæum (Månens nærmeste position til Jorden), når koefficienten sine højeste værdier. De allerstørste tidevand har tendens til at samle sig omkring forårs- og efterårsjævndøgn.',
        },
        { h2: 'Hvad en koefficient ikke fortæller dig' },
        {
          p: 'Koefficienten beskriver kun det astronomiske spænd. Den medregner ikke vejrets indflydelse: vind og lufttryk kan hæve eller sænke den faktiske vandstand uanset koefficienten. Den er desuden en egenskab ved tidevandet som helhed for den pågældende dag, ikke en højde i meter eller fod på et enkelt sted.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Flod, ebbe og strømstille: sådan fungerer tidevandsstrømme',
      description:
        'Sådan fungerer tidevandsstrømme: retningen på flod og ebbe, strømstille ved vendingen, strømhastighed i knob og strømretning, og hvorfor strømstille ikke altid falder sammen med højvande og lavvande.',
      blurb:
        'Flod, ebbe og strømstille, og hvorfor den stærkeste strøm ikke altid er ved højvande.',
      body: [
        {
          p: 'Tidevandsstrømme er den vandrette bevægelse af vand, der ledsager tidevandets lodrette stigning og fald. Hvor tidevandshøjden fortæller, hvor dybt vandet er, fortæller strømmen, hvilken vej det løber, og hvor hurtigt. Strømme betyder mest i render, indløb og flodmundinger, hvor de kan løbe med flere knob.',
        },
        { h2: 'Flod, ebbe og strømstille' },
        {
          list: [
            {
              term: 'Flodstrøm',
              text: 'vand, der løber ind mod kysten eller op i en flodmunding, som regel mens tidevandet stiger.',
            },
            {
              term: 'Ebbestrøm',
              text: 'vand, der løber ud mod havet eller ned gennem en flodmunding, som regel mens tidevandet falder.',
            },
            {
              term: 'Strømstille',
              text: 'den korte pause, hvor strømmen er nær nul, mens den vender mellem flod og ebbe.',
            },
          ],
        },
        {
          p: 'Strømhastigheden måles i knob (sømil i timen). Dens retning, kaldet strømretningen, angives som en kompaspejling: den retning, vandet løber hen imod.',
        },
        { h2: 'Strømstille er ikke altid ved højvande' },
        {
          p: 'En udbredt antagelse er, at strømmen standser ved højvande og lavvande. Det gælder kun visse steder. Tidspunktet afhænger af, hvordan tidevandsbølgen opfører sig lokalt. Hvor tidevandet opfører sig som en fremadskridende bølge, som i en lang åben rende, indtræffer den stærkeste flod og ebbe nær højvande og lavvande, og strømstille falder nær midten. Hvor det opfører sig som en stående bølge, som ved bunden af en lukket bugt, er det omvendt: den hurtigste strøm er nær midttidevand, og strømstille indtræffer nær højvande og lavvande. Lokale strømtabeller er den pålidelige vejledning.',
        },
        { h2: 'Vendende og roterende strømme' },
        {
          p: 'I smalle render er strømmen som regel vendende: den løber den ene vej under flod, vender så og løber tilbage under ebbe. I åbent kystvand er strømmen ofte roterende i stedet og skifter retning løbende hele kompasset rundt gennem en tidevandscyklus i stedet for at vende langs en enkelt linje.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Tidevandsreferencer og tidevandshøjder',
      description:
        'Hvad en tidevandshøjde måles fra: søkortnul, laveste astronomiske tidevand (LAT), middel af det lavere lavvande (MLLW), og hvorfor referencer er forskellige fra land til land.',
      blurb: 'Søkortnul, LAT, MLLW og hvad tallet i en tidevandshøjde egentlig betyder.',
      body: [
        {
          p: 'Enhver tidevandshøjde er en måling over et referenceniveau, der kaldes en reference. Når en tidevandstabel siger, at vandet når 1.8 meter, betyder det 1.8 meter over den lokale tidevandsreference: ikke over havbunden og ikke over havoverfladen i dagligdags forstand. At kende referencen er det, der gør en højde meningsfuld.',
        },
        { h2: 'Hvad en tidevandsreference er' },
        {
          p: 'En tidevandsreference er en referencehøjde, der defineres af et bestemt stadie af tidevandet, midlet over en lang periode: sædvanligvis cirka 19 år, hvilket udjævner den 18.6 år lange cyklus i Månens bane. Fordi den er et gennemsnit af virkelige observationer, er en reference specifik for et sted og for den myndighed, der har beregnet den.',
        },
        { h2: 'Søkortnul og gængse referencer' },
        {
          p: 'Søkort bruger en lavvandsreference, så den faktiske dybde næsten altid er mindst den kortlagte dybde. Dette søkortnul er det nulpunkt, hvorfra både de kortlagte dybder og tidevandshøjderne regnes. Forskellige lande anvender forskellige referencer:',
        },
        {
          list: [
            {
              term: 'LAT (laveste astronomiske tidevand)',
              text: 'det laveste niveau, der kan forudsiges under gennemsnitligt vejr; anvendes af Storbritannien og mange andre nationer.',
            },
            {
              term: 'MLLW (middel af det lavere lavvande)',
              text: 'gennemsnittet af det laveste af hver dags to lavvande; standardreferencen på søkort i USA.',
            },
            {
              term: 'MSL (middelvandstand)',
              text: 'den langsigtede gennemsnitlige vandstand, der hovedsagelig bruges som reference for landhøjder frem for til søkort.',
            },
          ],
        },
        {
          p: 'Højvandsreferencer såsom middelhøjvande (MHW) og middel af det højere højvande (MHHW) bruges til det modsatte formål, for eksempel til at fastlægge en kystlinje eller en frihøjde under en bro.',
        },
        { h2: 'Hvorfor højder ikke er direkte sammenlignelige' },
        {
          p: 'Fordi referencerne er forskellige, kan en højde ved én station ikke sammenlignes direkte med en højde ved en anden, medmindre begge bruger samme reference. Et lavvande på 0.5 meter målt fra LAT er ikke den samme vandstand som et lavvande på 0.5 meter målt fra MLLW. Det er også grunden til, at det samme fysiske tidevand kan beskrives med forskellige tal i forskellige landes tabeller.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Sådan beregnes tidevandsforudsigelser',
      description:
        'Hvordan tidevand forudsiges: tyngdepåvirkning fra Månen og Solen, harmoniske komponenter som M2 og S2, og hvorfor observerede vandstande afviger fra den astronomiske forudsigelse.',
      blurb:
        'Harmonisk analyse, komponenterne bag tidevandet, og hvorfor vejret får virkeligheden til at afvige.',
      body: [
        {
          p: 'Tidevand kan forudsiges år i forvejen, fordi det drives af Månens og Solens bevægelser, som kendes med stor præcision. Tidevandet på et sted er havets reaktion på denne tyngdepåvirkning, formet af den lokale kystlinje og dybde. Metoden, der omsætter astronomi til en tidevandstabel, kaldes harmonisk analyse.',
        },
        { h2: 'Tidevandet som en sum af komponenter' },
        {
          p: 'Harmonisk analyse betragter tidevandet som summen af mange enkle bølger, der hver kaldes en komponent. Hver komponent har en frekvens, der er fastlagt af astronomien (af hvordan Jorden, Månen og Solen bevæger sig i forhold til hinanden) mens dens amplitude og timing findes ved at tilpasse komponenterne til en periode med virkelige vandstandsobservationer på stedet. De vigtigste komponenter omfatter:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'den vigtigste halvdaglige månekomponent, periode 12.42 timer; som regel den største.',
            },
            { term: 'S2', text: 'den vigtigste halvdaglige solkomponent, periode 12.00 timer.' },
            { term: 'N2', text: 'en månekomponent, der stammer fra Månens elliptiske bane.' },
            {
              term: 'K1 og O1',
              text: 'de vigtigste daglige komponenter, der mange steder gør det ene daglige højvande større end det andet.',
            },
          ],
        },
        {
          p: 'Den velkendte cyklus mellem springflod og niptid opstår naturligt heraf: M2 og S2 har lidt forskellige perioder, så de kommer i og ud af takt: hver 14.8 dag forstærker de hinanden til springflod og ophæver delvist hinanden til niptid.',
        },
        { h2: 'Forudsigelse fremad' },
        {
          p: 'Når amplituden og fasen for hver komponent er kendt for et sted, er det at forudsige tidevandet blot et spørgsmål om at beregne summen på et hvilket som helst fremtidigt tidspunkt. De astronomiske frekvenser ændrer sig aldrig, så beregningen er deterministisk og kan køre årtier frem.',
        },
        { h2: 'Hvorfor det virkelige tidevand afviger' },
        {
          p: 'En forudsigelse er kun det astronomiske tidevand. Den medregner ikke vejret. Kraftig vind, lavt eller højt lufttryk, kraftig nedbør og vandføring fra floder kan hæve eller sænke den faktiske vandstand: en stormflod kan lægge en meter eller mere oven i forudsigelsen, mens højt tryk og fralandsvind kan holde vandet et godt stykke under den. Forudsigelser er derfor en fremragende rettesnor for tidevandets tidspunkt og størrelse, men den observerede vandstand på selve dagen kan afvige.',
        },
      ],
    },
  },
}
